import { KonvaEventObject } from "konva/lib/Node";
import React, { useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { registerToolRendererGroup } from "../../util/shapeCategorySingleton";
import { addItemToSelection, exitSelection, replaceShape } from "../../../../store/model";
import { getPointerModelPosition } from "../../util/stageManipulation";
import Hotspot from "../../Hotspot";
import { createCircleRubber } from "../../tools/CircleRubber";
import CircleBase from "./CircleBase";
import { Transform } from "../../../../geometry/transformation";
import { JSONGeometricShape } from "../../../Palettes/Headerbar/toolbarButtonOpen";
import { transformShape } from "../../util/shapeEdit";
import { RootState } from "../../../../store/store";
import { Segment } from "../line/Line";
import { angleOfTwoLinesFromThreePoints, lineCircleIntersection } from "../../../../geometry/operation";

interface CircleProps {
    shapeProps: CircleShape;
    onSelect: (e: KonvaEventObject<MouseEvent>) => void;
}
//interface Circle
const Circle = ({ shapeProps, onSelect }: CircleProps) => {


    const shapeTransformation = shapeProps.transformation;
    const [localTransform, setLocalTransform] = useState<Mat5>([[1, 0, 0, 0], [0, 1, 0, 0], [0, 0, 1, 0], [0, 0, 0, 1]])

    const dispatch = useAppDispatch();
    const selection = useAppSelector((state: RootState) => state.model.present.selected);
    const [dragStartPosition, setDragStartPosition] = useState<Point2d>({ x: shapeProps.x, y: shapeProps.y });
    const [centerPointHotspot] = useState<Hotspot>(centerHotspot());
    let shapeTransformer = new Transform(shapeTransformation);
    const selected = selection.selectedItemIds.includes(shapeProps.id);

    function centerHotspot(): Hotspot {
        return {
            x: shapeProps.x,
            y: shapeProps.y,
            id: (shapeProps.id ?? 0).toString(),
            isDragging: false,
            //possibleInteractions: ["move-object", "move-point"]
        }
    }

    // Movement handling
    const handleCircleDragStart = (e: KonvaEventObject<MouseEvent>) => {

        const id = e.target.id();
        dispatch(exitSelection());
        dispatch(addItemToSelection(parseInt(id)))
        setDragStartPosition(getPointerModelPosition(e));
    }

    const handleCircleDragMove = (e: KonvaEventObject<MouseEvent>) => {

        const pointerPos = getPointerModelPosition(e)
        const moveVector: Point2d = {
            x: pointerPos.x - dragStartPosition.x,
            y: pointerPos.y - dragStartPosition.y
        }
        const trafo = Transform.translationTransform(moveVector.x, moveVector.y);
        setLocalTransform(trafo.m);

    }

    const handleCircleDragEnd = (e: KonvaEventObject<MouseEvent>) => {
        setDragStartPosition({ x: 0, y: 0 })

        dispatchNewMovedObject();
        setLocalTransform([
            [1, 0, 0, 0],
            [0, 1, 0, 0],
            [0, 0, 1, 0],
            [0, 0, 0, 1]]);
    }


    const dispatchNewMovedObject = () => {
        const payload: CircleShape = {
            ...shapeProps,
            transformation: shapeTransformer.m
        }
        dispatch(replaceShape(payload));
    }

    const handleMiddleHotspotDragStart = (e: KonvaEventObject<MouseEvent>) => {
        setDragStartPosition(getPointerModelPosition(e));
    }



    const currentMouseInteraction: MouseInteraction = {
        handleDragStart: handleCircleDragStart,
        handleDragMove: handleCircleDragMove,
        handleDragEnd: handleCircleDragEnd,
    }


    shapeTransformer.multiply(new Transform(localTransform))

    return (
        <>
            <CircleBase shapeProps={{ ...shapeProps, transformation: shapeTransformer.m, }} onClick={onSelect} interaction={currentMouseInteraction} />
            {selected && (
                <Hotspot
                    key={`center-hotspot`}
                    shapeProps={{
                        ...centerPointHotspot,
                        ...shapeTransformer.point2d({ x: shapeProps.x, y: shapeProps.y })
                    }}
                    interaction={//currentMouseInteraction
                        {
                            handleDragStart: handleMiddleHotspotDragStart,
                            handleDragMove: handleCircleDragMove,
                            handleDragEnd: handleCircleDragEnd,
                        }
                    }
                />
            )}
        </>
    );


}


const createCircle = (
    shapeProps: GeometricShape,
    onSelect: (e: KonvaEventObject<MouseEvent>) => void,
) => {
    return <Circle
        key={shapeProps.id}
        shapeProps={shapeProps as CircleShape}
        onSelect={onSelect}
    />
};

const circleSnapPointCalculator = (s: GeometricShape) => {
    return { ...s, snapPoint: [(new Transform(s.transformation).point2d(s as CircleShape))] };
}
function circleSplitByLine(shape: GeometricShape, line: Segment): GeometricShape[] {
    const circleShape = shape as CircleShape;
    const transformedSectionline = getInvertedSectionLine(line, shape);
    const seg = {
        x1: transformedSectionline[0].x,
        y1: transformedSectionline[0].y,
        x2: transformedSectionline[1].x,
        y2: transformedSectionline[1].y
    };
    const intersections = lineCircleIntersection(seg, { x: circleShape.x, y: circleShape.y, r: circleShape.radius })
    if (intersections.length === 2) {

        console.log("line-circle int", intersections);
        const angleFromY = angleOfTwoLinesFromThreePoints({ x: 0, y: 0 }, intersections[0], { x: 1, y: 0 })
        const angleOfArc = angleOfTwoLinesFromThreePoints({ x: 0, y: 0 }, intersections[0], intersections[1])
        const angleTransform = new Transform().rotate(- angleFromY)
        const rotatedTransform = angleTransform.multiply(new Transform(circleShape.transformation));
        const angleInDegree = ( -angleOfArc *180 / Math.PI ) % 360;
        const arc1: CircleShape = {
            ...circleShape,
            startAngle: angleInDegree,
            clockwise: circleShape.mirrored, type: "circle",
            transformation: (rotatedTransform).m,
        }
        const arc2: CircleShape = {
            ...circleShape,
            startAngle: angleInDegree,
            clockwise: !circleShape.mirrored, type: "circle",
            transformation: (rotatedTransform).m,
        }
        return [arc1, arc2];
    }
    return [];
}
function getInvertedSectionLine(line: Segment, shape: GeometricShape) {
    const inverseTransform = new Transform(shape.transformation).invert2d();
    const transformedSectionline: Segment = [inverseTransform.point2d(line[0]), inverseTransform.point2d(line[1])];
    return transformedSectionline;
}

function transformCircleToInternalShape(s: JSONGeometricShape): GeometricShape {
    if (s.type === "circle") {
        const x = s.x;
        const y = s.y;
        s.x = 0;
        s.y = 0;
        const translatedShape = transformShape(s, Transform.translationTransform(x, y)) as CircleShape
        return translatedShape;
    }
    return s;
}

function globalCircle(s: GeometricShape) {
    const transform = new Transform(s.transformation);
    const arc = s as CircleShape;
    const { transformation, snapPoint, ...modifiedProps } = {
        ...arc,
        ...transform.point2d(arc),
        lineType: "todo",
    };
    //console.log(modifiedProps);

    return modifiedProps
}


registerToolRendererGroup("circle", { toolRender: createCircleRubber })
export { globalCircle, createCircle, createCircleRubber, circleSnapPointCalculator, circleSplitByLine, transformCircleToInternalShape }
export default Circle;